import React, {useContext, useEffect, useMemo, useState} from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Box,
    Button,
    Typography, LinearProgress,
} from "@mui/material";
import usePA_GlobalStore from "../../Services/ZustandGlobalStore";

// Import refactored tab components
import UnifiedBudgetPersonalBudget from "./UnifiedBudgetPersonalBudget";
import UnifiedBudgetBusinessAuthorization from "./UnifiedBudgetBusinessAuthorization";
import UnifiedBudgetBusinessExpenseReport from "./UnifiedBudgetBusinessExpenseReport";
import {PAContext} from "../../Services/PAContext";



export default function UnifiedBudget({ isOpen, onClose }) {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray } = useContext(PAContext);
    const { Account } = dataArray;

    // 1. Track the targeted attendee directly from the global store reactively.
    const currentAttendee = useMemo(() => {
        return PA_GlobalObject?.Planning?.attendees?.find(_att => _att.id === Account.id);
    }, [PA_GlobalObject.Planning.attendees, Account.id]);

    const attendeeBudget = currentAttendee?.budget || {};
    const totalBudgetAmount = parseFloat(attendeeBudget.total_budget || 0);
    const totalBudgetCurrency = attendeeBudget.currency || "USD";

    const [tabIndex, setTabIndex] = useState(0);

    // 2. 🟢 FIXED: Compute category summaries reactively via useMemo while stripping out backing receipts (via section_item)
    const categorySummaries = useMemo(() => {
        const summaries = {};
        const planning = PA_GlobalObject?.Planning;

        if (!isOpen || !Account.id || !planning) return summaries;

        const userIdStr = String(Account.id);

        // Helper helper to filter standalone receipts safely (section_item must be empty or missing)
        const isStandaloneReceipt = (item) => !item.section_item || String(item.section_item).trim() === "";

        // General
        const _general_list = (planning.receipts || []).filter(item =>
            String(item.attendee_id) === userIdStr &&
            item.section === "General" &&
            isStandaloneReceipt(item)
        );
        const general_total = _general_list.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        summaries.General = { name: 'General', list: _general_list, total: general_total };

        // Lodging
        const _lodging_list = (planning.lodging || []).filter(item =>
            item.attendees?.some(att => String(att.id) === userIdStr)
        );
        const lodging_total = _lodging_list.reduce((sum, item) => {
            const match = item.attendees?.find(att => String(att.id) === userIdStr);
            return sum + (match ? (parseFloat(match.cost) || 0) : 0);
        }, 0);
        summaries.Lodging = { name: 'Lodging', list: _lodging_list, total: lodging_total };

        // Meals
        const _meal_list = (planning.meals || []).filter(item =>
            item.attendees?.some(att => String(att.id) === userIdStr)
        );
        const meal_total = _meal_list.reduce((sum, item) => {
            const match = item.attendees?.find(att => String(att.id) === userIdStr);
            return sum + (match ? (parseFloat(match.cost) || 0) : 0);
        }, 0);
        summaries.Meals = { name: 'Meals', list: _meal_list, total: meal_total };

        // Activities
        const _activity_list = (planning.activities || []).filter(item =>
            item.attendees?.some(att => String(att.id) === userIdStr)
        );
        const activity_total = _activity_list.reduce((sum, item) => {
            const match = item.attendees?.find(att => String(att.id) === userIdStr);
            return sum + (match ? (parseFloat(match.cost) || 0) : 0);
        }, 0);
        summaries.Activities = { name: 'Activities', list: _activity_list, total: activity_total };

        // Flights
        const _flight_list = (planning.flights || []).filter(item =>
            item.attendees?.some(att => String(att.id) === userIdStr) || (item.attendee && String(item.attendee.id) === userIdStr)
        );
        const flight_total = _flight_list.reduce((sum, item) => {
            const match = item.attendees?.find(att => String(att.id) === userIdStr) || item.attendee;
            return sum + (match && String(match.id) === userIdStr ? (parseFloat(match.cost) || 0) : 0);
        }, 0);
        summaries.Flights = { name: 'Flights', list: _flight_list, total: flight_total };

        // Supplies
        const _supplies_list = (planning.receipts || []).filter(item =>
            String(item.attendee_id) === userIdStr &&
            item.section === "Supplies" &&
            isStandaloneReceipt(item)
        );
        const supplies_total = _supplies_list.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        summaries.Supplies = { name: 'Supplies', list: _supplies_list, total: supplies_total };

        // Shopping
        const _shopping_list = (planning.receipts || []).filter(item =>
            String(item.attendee_id) === userIdStr &&
            item.section === "Shopping" &&
            isStandaloneReceipt(item)
        );
        const shopping_total = _shopping_list.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        summaries.Shopping = { name: 'Shopping', list: _shopping_list, total: shopping_total };

        // Incidentals
        const _incidentals_list = (planning.receipts || []).filter(item =>
            String(item.attendee_id) === userIdStr &&
            item.section === "Incidentals" &&
            isStandaloneReceipt(item)
        );
        const incidentals_total = _incidentals_list.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        summaries.Incidentals = { name: 'Incidentals', list: _incidentals_list, total: incidentals_total };

        // Transportation
        const _transportation_list = (planning.receipts || []).filter(item =>
            String(item.attendee_id) === userIdStr &&
            item.section === "Transportation" &&
            isStandaloneReceipt(item)
        );
        const transportation_total = _transportation_list.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        summaries.Transportation = { name: 'Transportation', list: _transportation_list, total: transportation_total };

        return summaries;
    }, [isOpen, Account.id, PA_GlobalObject?.Planning]);

    // 3. Derived calculation total used across categories updates cleanly now
    const totalUsed = useMemo(() => {
        return Object.values(categorySummaries).reduce((sum, category) => {
            return sum + (category.total || 0);
        }, 0);
    }, [categorySummaries]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const getProgressColor = (used, limit) => {
        if (limit === 0) return 'primary';
        const percent = (used / limit) * 100;
        if (percent <= 90) return 'success';
        if (percent <= 100) return 'warning';
        return 'error';
    };

    const progressPercent = totalBudgetAmount > 0 ? (totalUsed / totalBudgetAmount) * 100 : 0;



    return (


        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            disableEnforceFocus={true}
            // 🚀 GLASSMORPHIC MODAL PAPERS SURFACE STYLING
            PaperProps={{
                sx: {
                    // 🌟 FIX 1: Brightened the base glass and kept the heavy blur only on the main window frame
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(15px)',
                    WebkitBackdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                    color: '#10318f'
                }
            }}
        >
            {/* HEADER SECTION */}
            <Box
                sx={{
                    display: 'flex',
                    // 🟢 FIXED: Column layout on mobile, horizontal row layout on desktop
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    // Stretch elements to full width on mobile, center vertically on desktop
                    alignItems: { xs: 'stretch', sm: 'center' },
                    padding: '20px 24px',
                    gap: { xs: 1.5, sm: 2 }, // Clean gap spacing when items stack
                    borderBottom: '1px solid rgba(16, 49, 143, 0.15)',
                    background: 'rgba(255, 255, 255, 0.25)',
                    zIndex: 1,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: '#10318f',
                        width: { xs: '100%', sm: 'auto' } // Takes full width on mobile screens
                    }}
                >
                    {Account.nickname}'s Budget
                </Typography>

                {/* Progress Tracker Box: Right-aligned on mobile lines */}
                <Box
                    sx={{
                        width: { xs: '100%', sm: '200px' }, // Fully fluid width on mobile viewports
                        alignSelf: { xs: 'flex-end', sm: 'auto' }, // Forces container boundary justification
                        maxWidth: { xs: '240px', sm: '100%' } // Stops progress bars from stretching too wide on landscape phones
                    }}
                >
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(progressPercent, 100)}
                        color={getProgressColor(totalUsed, totalBudgetAmount)}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }}
                    />
                    <Typography
                        variant="caption"
                        align="right"
                        display="block"
                        mt={0.5}
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                    >
                        Used: {totalBudgetCurrency} {totalUsed.toFixed(2)} / {totalBudgetCurrency} {totalBudgetAmount.toFixed(2)}
                    </Typography>
                </Box>
            </Box>
            {/* NAVIGATION TABS SUB-CONTAINER */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    zIndex: 1,
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.3)'
                }}
            >
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#10318f',
                            height: '3px',
                            borderRadius: '3px 3px 0 0'
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            color: 'rgba(16, 49, 143, 0.65)',
                            '&.Mui-selected': {
                                color: '#10318f',
                                fontWeight: 700
                            }
                        }
                    }}
                >
                    <Tab icon={<i className="icon-wallet" style={{ fontSize: '18px' }} />} label="Personal" />
                    <Tab icon={<i className="icon-shield-check" style={{ fontSize: '18px' }} />} label="Business" />
                    <Tab icon={<i className="icon-pie-chart" style={{ fontSize: '18px' }} />} label="Summary" />
                </Tabs>
            </Box>

            {/* MAIN SCROLLABLE CONTENT BODY */}
            <DialogContent
                dividers
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 3,
                    // Clear solid gray fallback; substitute with matching fluid backdrop gradient tint
                    backgroundColor: 'rgba(249, 249, 249, 0.25)',
                    borderTop: 'none',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.4)'
                }}
            >
                {tabIndex === 0 && (
                    <Box sx={{ p: 3, border: '1px solid rgba(255, 255, 255, 0.5)', borderRadius: 4, backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                        <UnifiedBudgetPersonalBudget />
                    </Box>
                )}

                {tabIndex === 1 && (
                    <Box sx={{ p: 3, border: '1px solid rgba(255, 255, 255, 0.5)', borderRadius: 4, backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                        <UnifiedBudgetBusinessAuthorization />
                    </Box>
                )}

                {tabIndex === 2 && (
                    <Box sx={{ p: 3, border: '1px solid rgba(255, 255, 255, 0.5)', borderRadius: 4, backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                        <UnifiedBudgetBusinessExpenseReport />
                    </Box>
                )}
            </DialogContent>

            {/* DIALOG ACTIONS FOOTER */}
            <DialogActions sx={{ padding: '16px 24px', background: 'rgba(255, 255, 255, 0.2)' }}>
                <Button
                    onClick={onClose}
                    color="secondary"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 3,
                        borderRadius: '10px',
                        color: '#10318f'
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}